import { Component, createRef, type MutableRefObject } from "react";

import validationSchemas from "../../utils/validationSchemas";
import FieldsMapper from "../fieldsMapper/FieldsMapper";
import FileUploadSection from "../fileUploadSection/FileUploadSection";
import PreviewTable from "../previewTable/PreviewTable";

import { TemplateTypes } from "../../enums";
import TaskActions from "../../enums/TaskActions";
import dataService from "../../features/Application/services/dataServices/typedDataService";
import backgroundTasksEventsEmitter from "../../features/BackgroundTasks/events/backgroundTasksEventsEmitter";
import { type FileTask } from "../../features/BackgroundTasks/taskPool";
import importUsersTasks from "../../features/People/Users/UsersOverview/backgroundTasks/importUsersTasks";
import { type NotifyStepSettings, type WizardStepHandlers } from "../../interfaces";
import fileUtils, { type AcceptCancelationHandler, type OnUploadProgressHandler } from "../../utils/fileUtils";

import { initialNotifyConfigDefault } from "../../features/SystemNotifications/config";
import {
  withNotifyConfig,
  type WithNotifyConfigProps,
} from "../../features/SystemNotifications/containers/withNotifyConfig";
import { type NotifySettings } from "../../features/SystemNotifications/types";
import { NotifyStep } from "../assignmentModals/commonSteps";
import { NotifyStepSwitch } from "../notifyStep/NotifyStepSwitch";

import styles from "./usersFileUploadModal.module.scss";
import WizardModal from "../wizard/WizardModal/WizardModal";
import Section from "../wizard/Section";
import { noop } from "lodash";
import type { StartUsersImportResponse } from "../../features/People/types";
import LicenseConfirmationInfo from "features/Licensing/ContentAssignmentModalLicensingSteps/LicenseConfirmationModalContent/licenseConfirmationInfo/LicenseConfirmationInfo";
import { Context } from "../../enums/licenseContext";
import type { FileUploadState } from "../../interfaces/FileUploadState";
import type { BackgroundTaskHandlers } from "../../interfaces/BackgroundTaskHandlers";
import { type MappedField, type MapToField } from "interfaces/MappedField";

enum ImportUsersSteps {
  UploadFile = 0,
  MapFields,
  Preview,
  License,
  Notify,
}

export interface UsersFileUploadModalProps extends FileUploadState, BackgroundTaskHandlers {
  groupId?: number;
  modalTitle?: string;
  notifyLabelText?: string;
  uploadUsersFile: (
    accountId: number,
    actorId: number,
    contentType: string,
    file: File,
    onUploadProgressHandler: OnUploadProgressHandler,
    acceptCancellationHandler: AcceptCancelationHandler,
  ) => void;
  updateUsersFileUploadProgress: () => void;
  cancelUsersFileUpload: () => void;
  resetUsersFileUpload: () => void;
  getUploadedFileColumns: (fileName: string) => void;
  getUsersImportPreviewData: (fileName: string, previewItemsCount: number, arr: any[]) => void;
  resetUsersImportPreviewData: () => void;
  showGeneralLoaderWithTimeout: (timeout: number, cb: () => void) => void;
  uploadedUsersFileColumns: any;
  usersImportPreviewData: any;
  showModal: boolean;
  showLicenceStep?: boolean;
  showNotifyStep?: boolean;
  accountId: number;
  actorId: number;
  onClose: () => void;
  startUsersImport: (
    fileName: string,
    mappedFields: { [key: string]: any },
    notificationSettings?: NotifyStepSettings,
  ) => Promise<StartUsersImportResponse>;
}
export type Props = UsersFileUploadModalProps & WithNotifyConfigProps<NotifySettings>;

interface UsersFileUploadModalState {
  mappedFields: MappedField[];
  titlesForPreview: string[];
  customFields: MapToField[];
  fileName: string;
  originalFileName: string;
  isNotifyStepLoaded: boolean;
  isNotifyStepValid: boolean;
  isAccountPackLicenseExpired: boolean;
}

export class UsersFileUploadModal extends Component<Props, UsersFileUploadModalState> {
  state = {
    mappedFields: [] as MappedField[],
    titlesForPreview: [] as string[],
    customFields: [] as MapToField[],
    isNotifyStepLoaded: false,
    isNotifyStepValid: false,
    fileName: "",
    originalFileName: "",
    isAccountPackLicenseExpired: false,
  };

  onPreviousNotifyStepRef = createRef() as MutableRefObject<(() => Promise<void>) | undefined>;
  getNotifySettingsRef = createRef() as MutableRefObject<(() => Promise<NotifyStepSettings | undefined>) | undefined>;

  uploadCancellationHandler: { abort: () => void } | null = null;

  requiredMapToFields = [
    { name: "Email", isRequired: true },
    { name: "First Name", isRequired: true },
    { name: "Last Name", isRequired: true },
  ];

  allMapToFields = [
    ...this.requiredMapToFields,
    { name: "ID" },
    { name: "Department" },
    { name: "Job Title" },
    { name: "Country" },
  ];

  previewRowDataSequenceAndOptions = [
    { name: "id", clamp: 1 },
    { name: "email", clamp: 1 },
    { name: "firstName", clamp: 1 },
    { name: "lastName", clamp: 1 },
    { name: "department", clamp: 1 },
    { name: "jobTitle", clamp: 1 },
    { name: "country", clamp: 1 },
  ];

  componentDidUpdate(prevProps: Readonly<Props>) {
    if (prevProps.showModal !== this.props.showModal && !this.props.showModal) {
      this.props.notifyConfigPayload.resetNotifyConfig();
    }
  }

  onCloseModal = () => {
    const { onClose, isFileUploading, resetUsersFileUpload, resetUsersImportPreviewData, onFileDisposed } = this.props;
    if (isFileUploading) {
      this.cancelUsersFileUpload();
    }
    onClose();
    resetUsersFileUpload();
    resetUsersImportPreviewData();
    onFileDisposed();
  };

  cancelUsersFileUpload = () => {
    this.props.cancelUsersFileUpload();
    this.uploadCancellationHandler?.abort();
  };

  acceptCancellationHandler = (uploadCancellationHandler: { abort: () => void }) => {
    this.uploadCancellationHandler = uploadCancellationHandler;
  };

  areAllRequiredFieldsMapped = (mapToFields: MapToField[]) => {
    const { mappedFields } = this.state;

    const requiredFieldNames = mapToFields.filter((field) => field.isRequired).map((field) => field.name);

    return (
      mappedFields.length > 0 &&
      !mappedFields.some((field) => requiredFieldNames.some((name) => field.mapTo === name) && !field.mapFrom)
    );
  };

  getMappedFieldsForApiRequest = () =>
    this.state.mappedFields
      .filter((f) => f.mapFrom && f.mapTo && !f.isSkipped)
      .map((f) => ({
        mapFrom: f.mapFrom,
        mapTo: f.mapTo,
        isCustomField: f.isCustomField,
      }));

  checkUploadStatus = () => {
    const { uploadProgress, uploadError, isFileUploading, uploadedUsersFileColumns } = this.props;

    return (
      uploadProgress !== 100 ||
      uploadError ||
      isFileUploading ||
      uploadedUsersFileColumns.isLoading ||
      uploadedUsersFileColumns.items === 0
    );
  };

  onFinish = async () => {
    const { fileName, originalFileName } = this.state;
    const notifySettings = await this.getNotifySettingsRef?.current?.();
    const {
      addBackgroundTask,
      removeBackgroundTask,
      cancelBackgroundTask,
      showGeneralLoaderWithTimeout,
      startUsersImport,
    } = this.props;
    showGeneralLoaderWithTimeout(3000, async () => {
      const data = await startUsersImport(fileName, this.getMappedFieldsForApiRequest(), notifySettings);

      addBackgroundTask(importUsersTasks.InProgress(data.id, originalFileName));
      backgroundTasksEventsEmitter.updateHandlerOnce(TaskActions.close, data.id, removeBackgroundTask);
      backgroundTasksEventsEmitter.updateHandlerOnce(TaskActions.cancel, data.id, async (taskId: number) => {
        await dataService.users.cancelUsersImportTask(data);
        cancelBackgroundTask(taskId);
      });
      backgroundTasksEventsEmitter.updateHandler(TaskActions.click, data.id, (taskId: number) => {
        if ((this.props.backgroundTasks[taskId] as FileTask).fileUri) {
          fileUtils.downloadFile((this.props.backgroundTasks[taskId] as FileTask).fileUri);
        }
      });
    });

    this.onCloseModal();
  };

  onMappingChange = (newMappedFields: MappedField[]) => {
    const titlesForPreview = this.getTitlesForPreview(newMappedFields, this.allMapToFields);

    this.setState({
      mappedFields: newMappedFields,
      titlesForPreview,
    });
  };

  onFileChange = (fileName: string, originalFileName: string) => {
    const { getUploadedFileColumns } = this.props;
    getUploadedFileColumns(fileName);
    this.setState({
      mappedFields: [],
      fileName,
      originalFileName,
      customFields: [],
    });
  };

  onCustomFieldChange = (newCustomFields: MapToField[]) => {
    this.setState({
      customFields: newCustomFields,
    });
  };

  getTitlesForPreview = (mappedFields: MappedField[], mapToFields: MapToField[]) => {
    const titlesForPreview = mappedFields.filter((f) => f.mapFrom && f.mapTo && !f.isSkipped).map((f) => f.mapTo);

    return mapToFields.map((f) => f.name).filter((name) => titlesForPreview.includes(name));
  };

  getDroppedFiles() {
    const { droppedFile } = this.props;
    return !droppedFile ? [] : [droppedFile];
  }

  setIsDataValid = (isNotifyStepValid: boolean) => {
    this.setState({ isNotifyStepValid });
  };

  setIsDataLoaded = (isNotifyStepLoaded: boolean) => {
    this.setState({ isNotifyStepLoaded });
  };

  getStepActions = (
    showLicenceStep: boolean,
    showNotifyStep: boolean,
  ): { [key in ImportUsersSteps]: WizardStepHandlers } => {
    const stepActions: { [key in ImportUsersSteps]?: WizardStepHandlers } = {
      [ImportUsersSteps.UploadFile]: {
        onNext: this.props.onFileDisposed,
      },
      [ImportUsersSteps.MapFields]: {
        onNext: () =>
          this.props.getUsersImportPreviewData(this.state.fileName, 11, this.getMappedFieldsForApiRequest()),
      },
      [ImportUsersSteps.Preview]: {
        onPrevious: () => this.props.resetUsersImportPreviewData(),
      },
    };

    showLicenceStep && (stepActions[ImportUsersSteps.License] = {});
    showNotifyStep &&
      (stepActions[ImportUsersSteps.Notify] = {
        onPrevious: () => {
          this.onPreviousNotifyStepRef?.current?.();
          this.props.getUsersImportPreviewData(this.state.fileName, 11, this.getMappedFieldsForApiRequest());
        },
      });

    return stepActions as { [key in ImportUsersSteps]: WizardStepHandlers };
  };

  setIsAccountPackLicenseExpired = (isAccountPackLicenseExpired: boolean) => {
    this.setState({ isAccountPackLicenseExpired });
  };

  render() {
    const {
      showModal,
      accountId,
      actorId,
      isFileUploading,
      uploadProgress,
      uploadError,
      uploadUsersFile,
      updateUsersFileUploadProgress,
      uploadedUsersFileColumns,
      usersImportPreviewData,
      notifyConfig,
      showLicenceStep,
      showNotifyStep,
      groupId,
      notifyConfigPayload: { setNotifyConfig, shouldNotify, communicationChannel },
    } = this.props;

    const { mappedFields, titlesForPreview, customFields, isNotifyStepLoaded, isNotifyStepValid } = this.state;

    if (!showModal) return null;
    return (
      <WizardModal
        title={this.props.modalTitle || "Import users"}
        isOpen={showModal}
        onClose={this.onCloseModal}
        stepActions={this.getStepActions(!!showLicenceStep, !!showNotifyStep)}
        onFinish={this.onFinish}
      >
        <Section label="Upload File" required isLocked={this.checkUploadStatus()}>
          <FileUploadSection
            accountId={accountId}
            actorId={actorId}
            isUploading={isFileUploading}
            uploadProgress={uploadProgress}
            uploadError={uploadError}
            uploadFile={uploadUsersFile}
            initialFiles={this.getDroppedFiles()}
            cancelFileUpload={this.cancelUsersFileUpload}
            updateProgress={updateUsersFileUploadProgress}
            acceptFileFormat=".csv"
            label="File (.csv)"
            propertyName="files"
            validationSchema={validationSchemas.usersFileUpload}
            acceptCancellationHandler={this.acceptCancellationHandler}
            onFileChange={this.onFileChange}
          >
            <p>
              Download the&nbsp;
              <span className={styles.link} onClick={dataService.users.getImportFileTemplate} title="Download">
                template&nbsp;
              </span>
              to ensure your file is correctly formatted.
            </p>
          </FileUploadSection>
        </Section>

        <Section label="Map Fields" required isLocked={!this.areAllRequiredFieldsMapped(this.allMapToFields)}>
          <FieldsMapper
            mapFromColumnTitle="CSV Column"
            mapToColumnTitle="Field Name"
            mapFromFields={uploadedUsersFileColumns.items}
            mapToFields={
              uploadedUsersFileColumns.items.length > this.requiredMapToFields.length
                ? this.allMapToFields
                : this.requiredMapToFields
            }
            onMappingChange={this.onMappingChange}
            onCustomFieldChange={this.onCustomFieldChange}
            mappedFields={mappedFields}
            customFields={customFields}
            allowCustomFields
          />
        </Section>
        <Section
          label="Preview"
          isLocked={usersImportPreviewData.isLoading}
          isLockNavigation={usersImportPreviewData.isLoading}
        >
          <PreviewTable
            isGroup={!!groupId}
            titles={titlesForPreview}
            rowDataSequenceAndOptions={this.previewRowDataSequenceAndOptions}
            items={usersImportPreviewData.items}
            withGradient={usersImportPreviewData.itemsCount > 11}
            itemsAmount={usersImportPreviewData.itemsCount}
          />
        </Section>
        {showLicenceStep && (
          <Section label="Licensing" required isLocked={!this.state.isAccountPackLicenseExpired}>
            <LicenseConfirmationInfo
              onIsDataValidChange={this.setIsAccountPackLicenseExpired}
              licenseContext={Context.AddPeopleToGroups}
              info={{ groupIds: [groupId!], count: usersImportPreviewData.items.length }}
            />
          </Section>
        )}
        {showNotifyStep && (
          <Section
            label="Notify"
            preRender
            isLocked={notifyConfig.shouldNotifyUsers && (!isNotifyStepLoaded || !isNotifyStepValid)}
          >
            <div className={styles.notifyStep}>
              <NotifyStep
                renderModalActions={noop as any}
                onIsDataValidChange={this.setIsDataValid}
                onIsDataLoaded={this.setIsDataLoaded}
                isDataLoaded={isNotifyStepLoaded}
                onPreviousNotifyStepRef={this.onPreviousNotifyStepRef}
                getNotifySettingsRef={this.getNotifySettingsRef}
                notifyTemplateType={TemplateTypes.WelcomeEmail}
                renderSwitch={(switchProps) => (
                  <NotifyStepSwitch
                    labelText={this.props.notifyLabelText}
                    config={notifyConfig}
                    onNotifyConfigChange={setNotifyConfig}
                    switchProps={switchProps}
                  />
                )}
                shouldNotify={shouldNotify}
                communicationChannel={communicationChannel}
              />
            </div>
          </Section>
        )}
      </WizardModal>
    );
  }
}

const ConnectedComponent = withNotifyConfig(UsersFileUploadModal, initialNotifyConfigDefault);

export default ConnectedComponent;
